import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import JsBarcode from 'jsbarcode'
import { authenticatedFetchData, API_HOST } from '../../../lib'
import { logError } from '@tomra/datadog-browser-logging'
import { CreateLoginLinkDialog } from './dialogs/CreateLoginLinkDialog'
import { SendPasswordDialog } from './dialogs/SetPasswordDialog'
import { MoneyStatisticsTable } from './MoneyStatisticsTable'
import { UserPayouts } from './UserPayouts'
import { UserPendingBags } from './UserPendingBags'
import { Notes } from '../../common/notes/Notes'
import { hasEditUserPermission } from '../../../services/permission.service'
import { UserConsumerSessions } from './UserConsumerSessions'
import { DateWithUTCTooltip } from '../../common'
import { UserPhoneNumber } from './common/UserPhoneNumber'
import { UserEmail } from './common/UserEmail'
import { UserDelete } from './common/UserDelete'
import { UserEvents } from './UserEvents'
import { Button } from '@tomra/design-system-react/button'
import { SegmentedControl } from '@tomra/design-system-react/segmented-control'
import { Card } from '@tomra/design-system-react/card'

export const UserInfoPage = () => {
  const { userId } = useParams()
  const [userInfo, setUserInfo] = useState<UserInfoType>()
  const [requestStatus, setRequestStatus] = useState<string>('loading')
  const [activeTab, setActiveTab] = useState<string>('consumerSessions')
  const [showLoginLinkDialog, setShowLoginLinkDialog] = useState(false)
  const [showPasswordDialog, setShowPasswordDialog] = useState(false)

  useEffect(() => {
    if (userInfo?.barcode) {
      JsBarcode('#barcode-render', userInfo.barcode, {
        format: 'CODE128',
        height: 40,
        width: 2,
        background: 'white',
        displayValue: true,
        fontSize: 12
      })
    }
  }, [userInfo])

  useEffect(() => {
    const f = authenticatedFetchData(
      `${API_HOST}/mytomra/v1.0/hotlinetool/usersearch/id?userid=${encodeURIComponent(userId || '')}`
    )

    f.run().then(
      (userInfo: UserInfoType) => {
        setRequestStatus('')
        setUserInfo(userInfo)
      },
      (error: Error) => {
        if (error.name !== 'AbortError') {
          setRequestStatus('failed')
          logError(new Error('Failed to fetch user info'), error)
        }
      }
    )

    return () => f.abort()
  }, [userId])

  if (requestStatus === 'failed') {
    return (
      <div className="rounded-lg border-l-[3px] border-rose-500 bg-white p-4 text-left shadow-md dark:bg-gray-900 centerAbsolute">
        Whoopsie! Failed to fetch user info.
      </div>
    )
  }

  if (userInfo) {
    const { firstName, email, id, moneyStatistics, appContext, created } = userInfo

    return (
      <div className="p-6">
        <h1 className="text-xl mb-4">User Information</h1>

        <div className="grid grid-cols-12 gap-x-4">
          <div className="mr-2 col-span-4">
            <Card.Root className="mb-6">
              <Card.Content className="flex flex-col space-y-4">
                <dl>
                  <dt id="name" className="text-gray-600">
                    Name
                  </dt>
                  <dd aria-labelledby="name">{firstName}</dd>
                </dl>

                <UserEmail userInfo={userInfo} />

                <dl>
                  <dt id="userId" className="text-gray-600">
                    User ID
                  </dt>
                  <dd aria-labelledby="userId">{id}</dd>
                </dl>

                <div>
                  <p id="barcode" className="text-gray-600">
                    Barcode
                  </p>
                  <svg id="barcode-render">{id}</svg>
                </div>

                <dl>
                  <dt id="created" className="text-gray-600">
                    User created
                  </dt>
                  <dd aria-labelledby="created">{<DateWithUTCTooltip date={new Date(created)} />}</dd>
                </dl>

                <dl>
                  <dt id="appContext" className="text-gray-600">
                    App name
                  </dt>
                  <dd aria-labelledby="appContext">{appContext}</dd>
                </dl>

                <UserPhoneNumber userInfo={userInfo} />

                <UserDelete userInfo={userInfo} />

                {hasEditUserPermission() && (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start pt-6">
                    <Button onClick={() => setShowLoginLinkDialog(true)}>Create login link</Button>

                    <Button onClick={() => setShowPasswordDialog(true)}>Set password</Button>
                  </div>
                )}
              </Card.Content>
            </Card.Root>

            {showLoginLinkDialog && (
              <CreateLoginLinkDialog
                userId={id}
                appContext={appContext}
                onClose={() => setShowLoginLinkDialog(false)}
              />
            )}

            {showPasswordDialog && (
              <SendPasswordDialog
                userId={id}
                email={email}
                appContext={appContext}
                onClose={() => setShowPasswordDialog(false)}
              />
            )}

            <Notes sourceId={userId!} />
          </div>

          <div className="col-span-8">
            <SegmentedControl.Root value={activeTab} onValueChange={setActiveTab}>
              <SegmentedControl.List>
                <SegmentedControl.Trigger value="consumerSessions">Sessions</SegmentedControl.Trigger>
                <SegmentedControl.Trigger value="payouts">Payouts</SegmentedControl.Trigger>
                <SegmentedControl.Trigger value="moneyStatistics">Money statistics</SegmentedControl.Trigger>
                <SegmentedControl.Trigger value="pendingBags">Pending bags</SegmentedControl.Trigger>
                <SegmentedControl.Trigger value="userEvents">User events</SegmentedControl.Trigger>
              </SegmentedControl.List>
            </SegmentedControl.Root>

            <div className="rounded-2xl bg-white shadow-md dark:bg-gray-900 mt-6">
              {activeTab === 'consumerSessions' ? (
                <UserConsumerSessions />
              ) : activeTab === 'payouts' ? (
                <UserPayouts />
              ) : activeTab === 'pendingBags' ? (
                <UserPendingBags />
              ) : activeTab === 'moneyStatistics' ? (
                moneyStatistics.length > 0 ? (
                  moneyStatistics.map((statistics, i) => (
                    <MoneyStatisticsTable key={`st-${i}`} statistics={statistics} />
                  ))
                ) : (
                  <div className="p-4">This user has no statistics yet</div>
                )
              ) : activeTab === 'userEvents' ? (
                <UserEvents />
              ) : null}
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="centerAbsolute">
      <div className="loadingSpinner" aria-label="Please wait" />
    </div>
  )
}
